import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  collectStoreCheckoutProofItemIds,
  fetchStoreCheckoutShippingTimeline,
  resolveStoreCheckoutSummaryDeliveryDisplay,
} from './storeCheckoutShippingTimeline.utils';
import type { ICartItemDetail, ITimelineElement } from '@/common/interfaces/cart.interface';
import type { GetTimelineMutationFunction } from '@/common/interfaces/order.interface';

interface IUseStoreCheckoutShippingTimelineInput {
  cartItems: ICartItemDetail[];
  shipTypeKey: string;
  orderUntil: string;
  getTimeline: GetTimelineMutationFunction;
}

const buildProofItemIdsKey = (cartItems: ICartItemDetail[]): string =>
  collectStoreCheckoutProofItemIds(cartItems)
    .sort((left, right) => left - right)
    .join(',');

export const useStoreCheckoutShippingTimeline = ({
  cartItems,
  shipTypeKey,
  orderUntil,
  getTimeline,
}: IUseStoreCheckoutShippingTimelineInput) => {
  const [timelineData, setTimelineData] = useState<ITimelineElement[]>([]);
  const [isTimelineLoading, setIsTimelineLoading] = useState(false);
  const timelineRequestIdRef = useRef(0);

  const proofItemIdsKey = useMemo(() => buildProofItemIdsKey(cartItems), [cartItems]);

  const proofItemIds = useMemo(() => {
    if (!proofItemIdsKey) {
      return [];
    }

    return proofItemIdsKey.split(',').map((proofItemId) => Number(proofItemId));
  }, [proofItemIdsKey]);

  const loadTimeline = useCallback(
    async (includeCustomDate: boolean) => {
      if (proofItemIds.length === 0) {
        setTimelineData([]);
        return;
      }

      const requestId = timelineRequestIdRef.current + 1;
      timelineRequestIdRef.current = requestId;
      setIsTimelineLoading(true);

      try {
        const nextTimelineData = await fetchStoreCheckoutShippingTimeline(
          getTimeline,
          proofItemIds,
          includeCustomDate ? orderUntil : undefined
        );

        if (timelineRequestIdRef.current !== requestId) {
          return;
        }

        setTimelineData(nextTimelineData);
      } finally {
        if (timelineRequestIdRef.current === requestId) {
          setIsTimelineLoading(false);
        }
      }
    },
    [getTimeline, orderUntil, proofItemIds]
  );

  useEffect(() => {
    if (proofItemIdsKey.length === 0) {
      setTimelineData([]);
      return;
    }

    const shouldIncludeCustomDate = Boolean(orderUntil.trim()) && Boolean(shipTypeKey);

    if (!shipTypeKey && !shouldIncludeCustomDate) {
      return;
    }

    loadTimeline(shouldIncludeCustomDate);
  }, [loadTimeline, orderUntil, proofItemIdsKey, shipTypeKey]);

  const estimatedDeliveryRange = useMemo(
    () => resolveStoreCheckoutSummaryDeliveryDisplay(orderUntil, shipTypeKey, timelineData),
    [orderUntil, shipTypeKey, timelineData]
  );

  const refreshTimeline = useCallback(() => {
    if (proofItemIdsKey.length === 0) {
      setTimelineData([]);
      return;
    }

    const shouldIncludeCustomDate = Boolean(orderUntil.trim()) && Boolean(shipTypeKey);

    if (!shipTypeKey && !shouldIncludeCustomDate) {
      return;
    }

    loadTimeline(shouldIncludeCustomDate);
  }, [loadTimeline, orderUntil, proofItemIdsKey, shipTypeKey]);

  return {
    estimatedDeliveryRange,
    timelineData,
    isTimelineLoading,
    refreshTimeline,
  };
};
